/**
 * `useHead` — THE APP-WIDE CONNECTION AND THE POSITION IT IS AT.
 *
 * The shell mounts this ONCE, for the app's lifetime, and it does three things that nothing else can:
 *
 *  1. IT BRINGS THE SOCKET UP ON EVERY ROUTE, including the 404 and the wallet page. `attachStatusOnce`
 *     subscribes the status handler, and `MarketSocket.onStatus` calls `ensureOpen` — so the head frame
 *     arrives even when no view is subscribed to anything. Before this hook existed the ONLY attach was
 *     inside `useStitch`, so a route with no stitched view produced no head, and the freshness badge rendered
 *     its empty state forever — INDISTINGUISHABLE FROM A HEALTHY QUIET FEED, which is precisely the failure
 *     class this repo keeps paying for.
 *  2. IT IS THE HELD HANDLER THE IDLE RULE IS SCOPED AROUND. `MarketSocket`'s idle-close counts KEYED topics
 *     only, deliberately, so that this permanent status handler cannot keep a subscription-less socket alive
 *     forever. That means the socket legitimately CLOSES 30 s into a page with no live view, and the badge
 *     has to say so rather than freeze on a stale slot — which is why this hook returns the transport state
 *     beside the data.
 *  3. IT SEPARATES THE TWO QUESTIONS. `asOf` says WHERE we are; `head` says whether that position is healthy
 *     (`stalledSec`, `behind`, `shedLevel`) and only the server can measure any of them.
 *
 * IT READS THE STORES, IT DOES NOT OWN THEM. `stream/status.ts` is the one writer of both; this hook is the
 * React binding plus the transport state, so there is no second copy of the head-frame unpacking.
 */

import type { AsOf, Head } from "@kinetiq/wire";
import { useEffect, useState } from "react";
import { asOfStore, headStore } from "../stores/asOf.ts";
import { useStore } from "../stores/store.ts";
import { type MarketSocket, type SocketState, marketSocket } from "./socket.ts";
import { attachStatusOnce } from "./status.ts";

export interface HeadState {
  /** `null` until the first `status/head` frame. NULL IS NOT ZERO. */
  readonly asOf: AsOf | null;
  readonly head: Head | null;
  readonly socket: SocketState;
  /** The live counters object, read straight off the socket. NOT a copy: a second tally of the same events is
   *  a second number to disagree with, and these are exactly the repairs that must be visible to be trusted. */
  readonly counters: MarketSocket["counters"];
}

export function useHead(): HeadState {
  const socket = marketSocket();
  const asOf = useStore(asOfStore);
  const head = useStore(headStore);
  const [state, setState] = useState<SocketState>(socket.state);

  useEffect(() => {
    // Attaching is what OPENS the connection. It is idempotent per bus, so a second mount (React 18's
    // StrictMode double-invokes effects in development) does not attach twice — and it must not, because a
    // doubled revert handler halves the window a forked seed row can be caught in.
    attachStatusOnce();
    // `onStateChange` fires only on a CHANGE, so the current value is read once here as well: a component
    // mounting after the socket opened would otherwise render "connecting" until the next transition, which
    // on a healthy socket is never.
    setState(socket.state);
    return socket.onStateChange(setState);
  }, [socket]);

  return { asOf, head, socket: state, counters: socket.counters };
}
